import { useMemo } from 'react';
import {
  useExternalStoreRuntime,
  type ThreadMessageLike,
  type ExternalStoreThreadListAdapter,
} from '@assistant-ui/react';
import { useIvyChatStore } from '@/stores/ivyChatStore';
import type { UIMessage } from './messageParser';
import { isOutOfCreditsMessage, outOfCreditsPart } from '@/services/billingPaywallDetector';
import { errorPart } from './chatNotices';
import { stepErrorText } from './stepWords';

function deduplicateMessages(messages: UIMessage[]): UIMessage[] {
  const seen = new Set<string>();
  const seenToolIds = new Set<string>();
  return messages.filter(msg => {
    if (seen.has(msg.id)) return false;
    seen.add(msg.id);
    if (msg.type === 'tool_use' && msg.toolUseId) {
      if (seenToolIds.has(msg.toolUseId)) return false;
      seenToolIds.add(msg.toolUseId);
    }
    if (msg.type === 'tool_result' && msg.toolUseId) {
      if (seenToolIds.has(`r-${msg.toolUseId}`)) return false;
      seenToolIds.add(`r-${msg.toolUseId}`);
    }
    return true;
  });
}

export function mergeIntoTurns(messages: UIMessage[]): ThreadMessageLike[] {
  const deduped = deduplicateMessages(messages);
  const result: ThreadMessageLike[] = [];
  let currentAssistantParts: any[] = [];
  let currentAssistantId: string | null = null;
  let currentTimestamp: Date | null = null;

  const resultMap = new Map<string, UIMessage>();
  for (const msg of deduped) {
    if (msg.type === 'tool_result' && msg.toolUseId) {
      resultMap.set(msg.toolUseId, msg);
    }
  }

  function flushAssistant() {
    if (currentAssistantParts.length > 0 && currentAssistantId) {
      result.push({
        id: currentAssistantId,
        role: 'assistant',
        content: currentAssistantParts,
        createdAt: currentTimestamp || undefined,
      });
      currentAssistantParts = [];
      currentAssistantId = null;
      currentTimestamp = null;
    }
  }

  for (const msg of deduped) {
    switch (msg.type) {
      case 'chat': {
        if (msg.role === 'user') {
          flushAssistant();
          result.push({
            id: msg.id,
            role: 'user',
            content: [{ type: 'text', text: msg.content }],
            createdAt: new Date(msg.timestamp),
          });
        } else {
          if (!currentAssistantId) {
            currentAssistantId = msg.id;
            currentTimestamp = new Date(msg.timestamp);
          }
          if (msg.content.trim()) {
            currentAssistantParts.push({ type: 'text', text: msg.content });
          }
        }
        break;
      }

      case 'thinking': {
        if (!currentAssistantId) {
          currentAssistantId = msg.id;
          currentTimestamp = new Date(msg.timestamp);
        }
        currentAssistantParts.push({ type: 'reasoning', text: msg.content });
        break;
      }

      case 'tool_use': {
        if (!currentAssistantId) {
          currentAssistantId = msg.id;
          currentTimestamp = new Date(msg.timestamp);
        }
        const toolResult = msg.toolUseId ? resultMap.get(msg.toolUseId) : undefined;
        currentAssistantParts.push({
          type: 'tool-call',
          toolCallId: msg.toolUseId || msg.id,
          toolName: msg.toolName || 'unknown',
          args: sanitizeArgs(msg.toolInput || {}),
          result: toolResult?.isError ? stepErrorText(toolResult.content) : toolResult?.content,
          isError: toolResult?.isError || false,
        });
        break;
      }

      case 'tool_result': {
        break;
      }

      case 'result': {
        if (msg.costUsd || msg.durationMs) {
          if (!currentAssistantId) {
            currentAssistantId = msg.id;
            currentTimestamp = new Date(msg.timestamp);
          }
          const text = formatResultText(msg);
          if (text) {
            currentAssistantParts.push({ type: 'text', text });
          }
        }
        break;
      }

      case 'system': {
        if (isOutOfCreditsMessage(msg)) {
          if (!currentAssistantId) {
            currentAssistantId = msg.id;
            currentTimestamp = new Date(msg.timestamp);
          }
          currentAssistantParts.push({ type: 'text', text: outOfCreditsPart(msg) });
        } else if (msg.subtype === 'error') {
          if (!currentAssistantId) {
            currentAssistantId = msg.id;
            currentTimestamp = new Date(msg.timestamp);
          }
          currentAssistantParts.push({ type: 'text', text: errorPart(msg.content) });
        }
        break;
      }
    }
  }

  flushAssistant();

  return result;
}

function sanitizeArgs(input: Record<string, unknown>): Record<string, any> {
  return JSON.parse(JSON.stringify(input));
}

function formatResultText(msg: UIMessage): string {
  const parts: string[] = [];
  if (msg.durationMs) parts.push(`${(msg.durationMs / 1000).toFixed(1)}s`);
  return parts.length > 0 ? `---\n${parts.join(' \u00b7 ')}` : '';
}

export function useIvyRuntime() {
  const messages = useIvyChatStore(s => s.messages);
  const sessions = useIvyChatStore(s => s.sessions);
  const activeChatId = useIvyChatStore(s => s.activeChatId);
  const isStreaming = useIvyChatStore(s => s.isStreaming);
  const sendMessage = useIvyChatStore(s => s.sendMessage);
  const abortStream = useIvyChatStore(s => s.abortStream);
  const createSession = useIvyChatStore(s => s.createSession);
  const selectSession = useIvyChatStore(s => s.selectSession);
  const deleteSession = useIvyChatStore(s => s.deleteSession);

  const threadList: ExternalStoreThreadListAdapter = useMemo(() => ({
    threadId: activeChatId || undefined,
    threads: sessions.map(s => ({
      id: s.chatId,
      title: s.title || 'New Chat',
      status: 'regular' as const,
    })),
    onSwitchToThread: (threadId: string) => selectSession(threadId),
    onSwitchToNewThread: () => { createSession(); },
    onDelete: (threadId: string) => deleteSession(threadId),
  }), [sessions, activeChatId, selectSession, createSession, deleteSession]);

  const convertedMessages = useMemo(() => mergeIntoTurns(messages), [messages]);

  const runtime = useExternalStoreRuntime({
    messages: convertedMessages,
    convertMessage: (msg: ThreadMessageLike) => msg,
    isRunning: isStreaming,
    onNew: async (message) => {
      const textParts = message.content.filter(p => p.type === 'text');
      const text = textParts.map(p => ('text' in p ? p.text : '')).join('\n');
      if (text.trim()) {
        await sendMessage(text.trim());
      }
    },
    onCancel: async () => {
      await abortStream();
    },
    adapters: {
      threadList,
    },
  });

  return runtime;
}
